import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Phone, Menu, ChevronDown, Cog, LogOut, LayoutDashboard, Inbox } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { company, products, services } from "../mock/mock";
import { useAuth } from "../context/AuthContext";

const Logo = () => (
  <Link to="/" className="flex items-center gap-3 shrink-0">
    <span className="relative inline-flex items-center justify-center w-11 h-11">
      <svg viewBox="0 0 100 100" className="w-11 h-11">
        <polygon
          points="50,4 91,27 91,73 50,96 9,73 9,27"
          fill="none"
          stroke="#D80000"
          strokeWidth="6"
        />
      </svg>
      <Cog className="absolute w-5 h-5 text-[#1a1a1a]" strokeWidth={2.2} />
    </span>
    <span className="leading-tight">
      <span className="font-heading text-[19px] tracking-tight text-[#1a1a1a] font-bold">
        shiv shakti enterprises
      </span>
      <span className="block text-[11px] font-semibold text-[#1a1a1a]/80 tracking-wide">
        {company.tagline}
      </span>
    </span>
  </Link>
);

const HoverDropdown = ({ label, items, basePath }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button className="flex items-center gap-1 px-1 py-2 text-[15px] text-[#1a1a1a] hover:text-[#D80000]">
        {label}
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 w-72 rounded-md border border-gray-100 bg-white shadow-xl py-2 animate-fade-up">
          {items.map((item) => (
            <button
              key={item.slug}
              onClick={() => navigate(`${basePath}/${item.slug}`)}
              className="block w-full text-left px-4 py-2 text-sm text-[#1a1a1a] hover:bg-[#D80000] hover:text-white transition-colors"
            >
              {item.title}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (to) => pathname === to;

  return (
    <header
      className={`sticky top-0 z-40 w-full bg-white transition-shadow ${
        scrolled ? "shadow-md" : "shadow-sm"
      }`}
    >
      <div className="max-w-[1240px] mx-auto px-5 h-[84px] flex items-center justify-between gap-4">
        <Logo />

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-6">
          <a
            href={`tel:${company.phoneHref}`}
            className="flex items-center gap-2 bg-[#1a1a1a] text-white text-sm font-semibold px-4 py-2 rounded-md hover:bg-[#D80000]"
          >
            <Phone className="w-4 h-4" />
            {company.phone}
          </a>
          <Link to="/" className={`text-[15px] hover:text-[#D80000] ${isActive("/") ? "text-[#D80000]" : "text-[#1a1a1a]"}`}>Home</Link>
          <Link to="/about" className={`text-[15px] hover:text-[#D80000] ${isActive("/about") ? "text-[#D80000]" : "text-[#1a1a1a]"}`}>About</Link>
          <HoverDropdown label="Products We Manufacture" items={products} basePath="/products" />
          <HoverDropdown label="Services" items={services} basePath="/services" />
          <Link to="/facility" className={`text-[15px] hover:text-[#D80000] ${isActive("/facility") ? "text-[#D80000]" : "text-[#1a1a1a]"}`}>Our Facility</Link>
          <Link to="/contact" className={`text-[15px] hover:text-[#D80000] ${isActive("/contact") ? "text-[#D80000]" : "text-[#1a1a1a]"}`}>Contact</Link>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger className="outline-none">
                <Avatar className="w-9 h-9 border-2 border-[#D80000]">
                  <AvatarImage src={user.picture} alt={user.name} />
                  <AvatarFallback className="bg-[#D80000] text-white text-sm">
                    {user.name ? user.name.charAt(0) : "U"}
                  </AvatarFallback>
                </Avatar>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-2 border-b">
                  <p className="text-sm font-semibold truncate">{user.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>
                <DropdownMenuItem onClick={() => navigate("/dashboard")} className="cursor-pointer">
                  <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                </DropdownMenuItem>
                {user.is_admin && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} className="cursor-pointer">
                    <Inbox className="w-4 h-4 mr-2" /> Quote Inbox
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={logout} className="cursor-pointer text-[#D80000]">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button
              onClick={login}
              className="bg-[#D80000] text-white text-sm font-semibold px-4 py-2 rounded-md hover:bg-[#b00000]"
            >
              Sign In
            </button>
          )}
        </nav>

        {/* Mobile */}
        <div className="flex lg:hidden items-center gap-3">
          <a href={`tel:${company.phoneHref}`} className="bg-[#1a1a1a] text-white p-2 rounded-md">
            <Phone className="w-4 h-4" />
          </a>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger className="p-2 text-[#1a1a1a]">
              <Menu className="w-6 h-6" />
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] overflow-y-auto">
              <div className="mt-6 flex flex-col gap-1">
                <Link to="/" onClick={() => setMobileOpen(false)} className="py-3 border-b font-medium">Home</Link>
                <Link to="/about" onClick={() => setMobileOpen(false)} className="py-3 border-b font-medium">About</Link>
                <p className="pt-4 pb-1 text-xs font-bold uppercase text-[#D80000]">Products We Manufacture</p>
                {products.map((p) => (
                  <Link key={p.slug} to={`/products/${p.slug}`} onClick={() => setMobileOpen(false)} className="py-2 pl-2 text-sm text-[#1a1a1a]/80 border-b border-gray-100">{p.title}</Link>
                ))}
                <p className="pt-4 pb-1 text-xs font-bold uppercase text-[#D80000]">Services</p>
                {services.map((s) => (
                  <Link key={s.slug} to={`/services/${s.slug}`} onClick={() => setMobileOpen(false)} className="py-2 pl-2 text-sm text-[#1a1a1a]/80 border-b border-gray-100">{s.title}</Link>
                ))}
                <Link to="/facility" onClick={() => setMobileOpen(false)} className="py-3 border-b font-medium mt-2">Our Facility</Link>
                <Link to="/contact" onClick={() => setMobileOpen(false)} className="py-3 border-b font-medium">Contact</Link>
                {user && user.is_admin && (
                  <Link to="/admin" onClick={() => setMobileOpen(false)} className="py-3 border-b font-medium text-[#D80000]">Quote Inbox</Link>
                )}
                {user ? (
                  <button onClick={() => { setMobileOpen(false); logout(); }} className="mt-4 bg-[#1a1a1a] text-white py-2 rounded-md">Sign out</button>
                ) : (
                  <button onClick={() => { setMobileOpen(false); login(); }} className="mt-4 bg-[#D80000] text-white py-2 rounded-md">Sign In</button>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
